import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import Loader from "../../../../components/elements/Loader";
import { EmailsToHash } from "../../../../types/campaignTypes";

type Props = {
  emailsToHash: EmailsToHash[];
  areEmailsLoading: boolean;
};

const StyledTableHead = styled(TableHead)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  "& th": {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.brand.gray500,
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const DomainRow = styled(TableRow)(({ theme }: { theme: Theme }) => ({
  height: 48,
  cursor: "pointer",
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
  "& td": {
    padding: "0 16px",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
    fontSize: 13,
    color: theme.palette.brand.gray900,
  },
}));

const EmailSubRow = styled(TableRow)(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const EmailListItem = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "6px 16px 6px 52px",
  fontSize: 13,
  color: theme.palette.brand.gray500,
}));

const CountBadge = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  marginLeft: 6,
  padding: "1px 7px",
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 500,
  background: theme.palette.brand.gray100,
  color: theme.palette.brand.gray500,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const InfoNote = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.gray500,
  padding: "12px 16px",
  borderTop: `1px solid ${theme.palette.brand.gray200}`,
}));

const LoaderWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "32px 16px",
  gap: 12,
});

const LoaderText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
}));

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  marginRight: 4,
  color: theme.palette.brand.gray500,
}));

const DomainCell = styled("span")({
  display: "flex",
  alignItems: "center",
});

const EmailsTableRedesign: FC<Props> = ({ emailsToHash, areEmailsLoading }) => {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const hasGeneralDomain = emailsToHash.some(({ domain }) => domain === "general");

  const toggleExpand = (domain: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  if (areEmailsLoading) {
    return (
      <LoaderWrapper>
        <LoaderText>We're getting your company information. This may take a few minutes...</LoaderText>
        <Loader isAbsolutePosition={false} size={42} />
      </LoaderWrapper>
    );
  }

  if (emailsToHash.length === 0) return null;

  const sorted = [...emailsToHash].sort((a, b) => a.domain.localeCompare(b.domain));

  return (
    <div>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell>Domain</TableCell>
            <TableCell>Emails</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {sorted.map(({ domain, emails }) => {
            const isExpanded = expandedDomains.has(domain);
            const sortedEmails = [...emails].sort((a, b) => a.localeCompare(b));
            return (
              <React.Fragment key={domain}>
                <DomainRow onClick={() => toggleExpand(domain)}>
                  <TableCell>
                    <DomainCell>
                      <ExpandIconButton
                        size="small"
                        aria-label={isExpanded ? "collapse" : "expand"}
                        onClick={(e) => toggleExpand(domain, e)}
                      >
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </ExpandIconButton>
                      {domain}
                    </DomainCell>
                  </TableCell>
                  <TableCell>
                    <CountBadge>{emails.length}</CountBadge>
                  </TableCell>
                </DomainRow>
                <EmailSubRow>
                  <TableCell colSpan={2}>
                    <Collapse in={isExpanded} unmountOnExit>
                      {sortedEmails.map((email) => (
                        <EmailListItem key={email}>{email}</EmailListItem>
                      ))}
                    </Collapse>
                  </TableCell>
                </EmailSubRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {hasGeneralDomain && (
        <InfoNote>
          For CRM targeting, the minimum number of emails per domain is <strong>20</strong>. If this threshold is not
          met, emails are grouped under a general domain for targeting purposes.
        </InfoNote>
      )}
    </div>
  );
};

export default EmailsTableRedesign;
